import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { t } from './i18n/es.js';

const UPDATE_CHECK_MS = 60 * 60 * 1000;
/** Minimum time between two update checks triggered by the app coming back to the foreground. */
const FOREGROUND_CHECK_MS = 30 * 1000;

/**
 * Service worker registration and update notice (f0-mobile-pwa, docs/07 §7). The worker is
 * built by vite-plugin-pwa with `registerType: 'prompt'`: a new version waits until the user
 * accepts, then the page reloads with the new build. The local database lives in OPFS and is
 * not touched by the service worker, so nothing is lost across updates.
 *
 * Update checks: at start, whenever the app comes back to the foreground and every hour. An app
 * installed on iPhone is resumed rather than reloaded and iOS does not check the service worker
 * by itself when it is opened (seen in f0-mobile-pwa on iOS 26.6.1), so the app asks explicitly.
 */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      let lastCheck = 0;
      const check = () => {
        if (!navigator.onLine || Date.now() - lastCheck < FOREGROUND_CHECK_MS) return;
        lastCheck = Date.now();
        registration.update().catch(() => undefined);
      };
      check();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('pageshow', check);
      window.addEventListener('online', check);
      setInterval(check, UPDATE_CHECK_MS);
    },
  });

  useEffect(() => {
    if (!offlineReady) return;
    const timer = setTimeout(() => setOfflineReady(false), 6000);
    return () => clearTimeout(timer);
  }, [offlineReady, setOfflineReady]);

  if (needRefresh) {
    return (
      <div className="banner" role="status" data-testid="pwa-update">
        <span>{t.pwa.updateAvailable}</span>
        <button type="button" onClick={() => void updateServiceWorker(true)}>
          {t.pwa.update}
        </button>
        <button type="button" onClick={() => setNeedRefresh(false)}>
          {t.pwa.later}
        </button>
      </div>
    );
  }
  if (offlineReady) {
    return (
      <div className="banner" role="status" data-testid="pwa-offline-ready">
        <span>{t.pwa.offlineReady}</span>
      </div>
    );
  }
  return null;
}
